import {useEffect, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button} from '@/components/ui/button'
import {Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {Skeleton} from '@/components/ui/skeleton'
import {DataTable, type Column} from '@/components/data-table'
import {HelpIcon} from '@/components/help-icon'
import {ListCard, ListEmptyState} from '@/components/section-card'
import {PaginationBar} from '@/components/ui/pagination-bar'
import {PageHeading} from '@/components/page-heading'
import {NoAccessState} from '@/components/no-access-state'
import {useIsSystemUser} from '@/hooks/empresa/organizacion/use-businesses'
import {
  VALORES_PAGE_SIZE,
  useUpsertValorEconomico,
  useValoresEconomicos,
  type ValorEconomico,
} from '@/hooks/inci/consola/use-admin'
import {formatCopFull} from '@/lib/money'
import {translateApiError} from '@/lib/errors'

const dateFormat = new Intl.DateTimeFormat('es-CO', {year: 'numeric', month: 'short', day: 'numeric'})

const MIN_YEAR = 2000
const MAX_YEAR = 2100

function validate(t: (key: string) => string, year: number, uvtCop: string, smmlvCop: string, yearTaken: boolean) {
  if (!Number.isInteger(year) || year < MIN_YEAR || year > MAX_YEAR) return t('valoresEconomicos.form.yearInvalid')
  if (yearTaken) return t('valoresEconomicos.form.yearTaken')
  const uvt = Number(uvtCop)
  if (!uvtCop.trim() || !Number.isFinite(uvt) || uvt <= 0) return t('valoresEconomicos.form.uvtInvalid')
  if (smmlvCop.trim()) {
    const smmlv = Number(smmlvCop)
    if (!Number.isFinite(smmlv) || smmlv <= 0) return t('valoresEconomicos.form.smmlvInvalid')
  }
  return null
}

function ValorEconomicoDialog({
  target,
  existingYears,
  onClose,
}: {
  target: ValorEconomico | 'new' | null
  existingYears: number[]
  onClose: () => void
}) {
  const {t} = useTranslation()
  const upsert = useUpsertValorEconomico()
  const isNew = target === 'new'
  const editing = target !== null && target !== 'new' ? target : null
  const [year, setYear] = useState('')
  const [uvtCop, setUvtCop] = useState('')
  const [smmlvCop, setSmmlvCop] = useState('')
  const [error, setError] = useState<string | null>(null)

  // The dialog is controlled from outside (target !== null), so a programmatic
  // open must prefill here — Radix only reports user-driven opens via onOpenChange.
  useEffect(() => {
    if (target === null) return
    setError(null)
    if (editing) {
      setYear(String(editing.year))
      setUvtCop(String(editing.uvt_cop))
      setSmmlvCop(editing.smmlv_cop != null ? String(editing.smmlv_cop) : '')
    } else {
      setYear(String((existingYears[0] ?? new Date().getFullYear() - 1) + 1))
      setUvtCop('')
      setSmmlvCop('')
    }
    // existingYears identity does not matter for prefilling — only the target does
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target])

  function handleOpenChange(open: boolean) {
    if (!open) {
      setYear('')
      setUvtCop('')
      setSmmlvCop('')
      setError(null)
      upsert.reset()
      onClose()
    }
  }

  function submit() {
    const yearNum = Number(year)
    const yearTaken = !editing && existingYears.includes(yearNum)
    const validationError = validate(t, yearNum, uvtCop, smmlvCop, yearTaken)
    if (validationError) {
      setError(validationError)
      return
    }
    setError(null)
    upsert.mutate(
      {year: yearNum, uvt_cop: Number(uvtCop), smmlv_cop: smmlvCop.trim() ? Number(smmlvCop) : null},
      {onSuccess: () => handleOpenChange(false), onError: (err) => setError(translateApiError(err))}
    )
  }

  return (
    <Dialog open={target !== null} onOpenChange={handleOpenChange}>
      <DialogContent data-testid="valor-economico-dialog">
        <DialogHeader>
          <DialogTitle>
            {isNew
              ? t('valoresEconomicos.form.addTitle')
              : t('valoresEconomicos.form.editTitle', {year: editing?.year})}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="valor-year">{t('valoresEconomicos.form.yearLabel')}</Label>
            <Input
              id="valor-year"
              data-testid="valor-year-input"
              type="number"
              min={MIN_YEAR}
              max={MAX_YEAR}
              value={year}
              disabled={!!editing}
              onChange={(e) => setYear(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="valor-uvt">{t('valoresEconomicos.form.uvtLabel')}</Label>
            <Input
              id="valor-uvt"
              data-testid="valor-uvt-input"
              type="number"
              min={1}
              value={uvtCop}
              onChange={(e) => setUvtCop(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="valor-smmlv">{t('valoresEconomicos.form.smmlvLabel')}</Label>
            <Input
              id="valor-smmlv"
              data-testid="valor-smmlv-input"
              type="number"
              min={1}
              value={smmlvCop}
              onChange={(e) => setSmmlvCop(e.target.value)}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button type="button" data-testid="valor-economico-submit" disabled={upsert.isPending} onClick={submit}>
            {upsert.isPending ? t('common.saving') : t('valoresEconomicos.form.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export function AdminValoresEconomicosPage() {
  const {t} = useTranslation()
  const isSystemUser = useIsSystemUser()
  const [page, setPage] = useState(1)
  const {data, isLoading} = useValoresEconomicos(isSystemUser, page)
  const [dialogTarget, setDialogTarget] = useState<ValorEconomico | 'new' | null>(null)

  if (!isSystemUser) return <NoAccessState />

  const rows = data?.items ?? []
  // Solo ve los años de la página a la vista: el año nuevo cae aquí por el orden del servidor, pero
  // pasadas veinte configuraciones el PUT (upsert) sobrescribiría un año viejo repetido.
  const existingYears = rows.map((r) => r.year)

  const columns: Column<ValorEconomico>[] = [
    {
      key: 'year',
      header: t('valoresEconomicos.table.year'),
      width: 'w-24',
      cell: (row) => <span className="font-mono text-sm font-medium text-foreground">{row.year}</span>,
    },
    {
      key: 'uvt',
      header: (
        <span className="inline-flex items-center gap-1">
          {t('valoresEconomicos.table.uvt')}
          <HelpIcon label={t('valoresEconomicos.table.uvtTipLabel')} testId="valores-uvt-tip">
            {t('valoresEconomicos.table.uvtTip')}
          </HelpIcon>
        </span>
      ),
      width: 'w-40',
      cell: (row) => <span className="font-mono text-sm text-foreground">{formatCopFull(row.uvt_cop)}</span>,
    },
    {
      key: 'smmlv',
      header: (
        <span className="inline-flex items-center gap-1">
          {t('valoresEconomicos.table.smmlv')}
          <HelpIcon label={t('valoresEconomicos.table.smmlvTipLabel')} testId="valores-smmlv-tip">
            {t('valoresEconomicos.table.smmlvTip')}
          </HelpIcon>
        </span>
      ),
      width: 'w-40',
      cell: (row) =>
        row.smmlv_cop != null ? (
          <span className="font-mono text-sm text-foreground">{formatCopFull(row.smmlv_cop)}</span>
        ) : (
          <span className="text-sm text-muted-foreground" data-testid={`smmlv-sin-configurar-${row.year}`}>
            {t('valoresEconomicos.table.smmlvSinConfigurar')}
          </span>
        ),
    },
    {
      key: 'audit',
      header: t('valoresEconomicos.table.audit'),
      width: 'w-56',
      cell: (row) =>
        row.updated_at ? (
          <span className="text-xs text-muted-foreground">
            {t('valoresEconomicos.table.auditLine', {
              name: row.updated_by
                ? `${row.updated_by.name} ${row.updated_by.last_name ?? ''}`.trim()
                : t('valoresEconomicos.table.auditUnknown'),
              date: dateFormat.format(new Date(row.updated_at)),
            })}
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        ),
    },
    {
      key: 'actions',
      header: '',
      width: 'w-28',
      className: 'text-right',
      cell: (row) => (
        <Button
          size="sm"
          variant="outline"
          data-testid={`valor-editar-${row.year}`}
          onClick={() => setDialogTarget(row)}>
          {t('valoresEconomicos.table.edit')}
        </Button>
      ),
    },
  ]

  return (
    <div className="space-y-5" data-testid="admin-valores-economicos">
      <PageHeading
        title={t('valoresEconomicos.title')}
        accion={
          rows.length > 0 && (
            <Button size="sm" data-testid="valor-agregar-anio" onClick={() => setDialogTarget('new')}>
              {t('valoresEconomicos.addYear')}
            </Button>
          )
        }
      />

      {isLoading && !data ? (
        <Skeleton className="h-64 w-full" />
      ) : (
        <ListCard
          footer={
            !!data?.total && (
              <PaginationBar page={page} pageSize={VALORES_PAGE_SIZE} total={data.total} onPageChange={setPage} />
            )
          }>
          <DataTable
            columns={columns}
            rows={rows}
            rowKey={(row) => String(row.year)}
            emptyState={
              <ListEmptyState
                page
                title={t('valoresEconomicos.empty')}
                action={
                  <Button size="sm" data-testid="valor-agregar-anio" onClick={() => setDialogTarget('new')}>
                    {t('valoresEconomicos.addYear')}
                  </Button>
                }
              />
            }
          />
        </ListCard>
      )}

      <ValorEconomicoDialog target={dialogTarget} existingYears={existingYears} onClose={() => setDialogTarget(null)} />
    </div>
  )
}
